import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Avatar from '@appchoose/avatar';
import Button from '@appchoose/button';
import Icon from '@appchoose/icon';
import { Modal, ModalContent } from '@appchoose/modal';
import { SidebarTitle } from '@appchoose/sidebar';
import { toast } from '@appchoose/toast';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { brandState } from '../../stores/brand';
import { activeSaleState } from '../../stores/sales';
import { StoreCurrency } from '../../types/generated';
import {
  SupplierBillingAdjustmentChargeTo,
  useDeleteSupplierBillingAdjustmentMutation,
  useDeleteSupplierBillingAdjustmentNoteMutation,
  useSupplierBillingAdjustmentQuery,
  useValidateSupplierBillingAdjustmentMutation,
} from '../../types/generated-new';
import { isScopeAuthorized } from '../../utils/auth';
import { formatPrice } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import { formatPercentage } from '../../utils/number';
import { ConfirmDeleteFinancialAdjustmentModal } from './confirm-delete-financial-adjustment-modal';
import { ConfirmDeleteFinancialAdjustmentNoteModal } from './confirm-delete-financial-adjustment-note-modal';
import { FinancialAdjustmentForm } from './financial-adjustment-form';
import { FinancialAdjustmentNoteForm } from './financial-adjustment-note-form';

type FinancialAdjustmentModalProps = {
  adjustmentId?: string;
  isEditModeDefault: boolean;
  setOpen: (isOpen: boolean) => void;
};

export const FinancialAdjustmentModal: React.FC<
  FinancialAdjustmentModalProps
> = ({ adjustmentId, isEditModeDefault: isEditDefault, setOpen }) => {
  const { user } = useAuth0();
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(isEditDefault);

  const brand = useAtomValue(brandState);
  const activeSale = useAtomValue(activeSaleState);
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);
  const [isOpenDeleteNoteConfirmation, setIsOpenDeleteNoteConfirmation] =
    useState(false);

  const isInternalUser = !!user?.['https://appchoose.io/claims/isInternalUser'];

  const { mutateAsync: mutateDeleteAsync } =
    useDeleteSupplierBillingAdjustmentMutation();
  const { mutateAsync: mutateValidateAsync } =
    useValidateSupplierBillingAdjustmentMutation();
  const { mutateAsync: mutateDeleteNoteAsync } =
    useDeleteSupplierBillingAdjustmentNoteMutation();
  const { data } = useSupplierBillingAdjustmentQuery(
    {
      supplierBillingAdjustmentId: adjustmentId ?? '',
      withNotes: isInternalUser,
    },
    {
      enabled: !!adjustmentId,
      select: (data) => data.supplierBillingAdjustment,
    }
  );

  const onValidate = async () => {
    try {
      await mutateValidateAsync({
        supplierBillingAdjustmentId: adjustmentId ?? '',
      });

      queryClient.invalidateQueries({
        queryKey: ['supplierBillingAdjustments', { saleId: activeSale?.id }],
      });

      queryClient.invalidateQueries({
        queryKey: ['billing', { saleId: activeSale?.id }],
      });

      setOpen(false);

      toast.success(t('invoice.financial_adjustment.validate_success'));
    } catch (e) {
      if (e instanceof Error) {
        switch (e.message) {
          case 'supplier_billing_adjustment_validation_error_missing_title':
            toast.error(
              t(
                'invoice.financial_adjustment_form.fields.title.validation_errors.required'
              )
            );
            return;
          case 'supplier_billing_adjustment_validation_error_missing_description':
            toast.error(
              t(
                'invoice.financial_adjustment_form.fields.description.validation_errors.required'
              )
            );
            return;
          case 'supplier_billing_adjustment_validation_error_missing_vat_rate':
            toast.error(
              t(
                'invoice.financial_adjustment_form.fields.vat_rate.validation_errors.required'
              )
            );
            return;
          case 'supplier_billing_adjustment_validation_error_missing_amount_vat_excluded_in_cents':
            toast.error(
              t(
                'invoice.financial_adjustment_form.fields.amount_vat_excluded.validation_errors.required'
              )
            );
            return;
          case 'supplier_billing_adjustment_validation_error_missing_amount_vat_inluded_in_cents':
            toast.error(
              t(
                'invoice.financial_adjustment_form.fields.amount_vat_included.validation_errors.required'
              )
            );
            return;
        }
      }
      toast.error(t('invoice.financial_adjustment.validate_error'));
    }
  };

  const onDelete = async () => {
    try {
      await mutateDeleteAsync({
        supplierBillingAdjustmentId: adjustmentId ?? '',
      });

      queryClient.invalidateQueries({
        queryKey: ['supplierBillingAdjustments', { saleId: activeSale?.id }],
      });

      queryClient.invalidateQueries({
        queryKey: ['billing', { saleId: activeSale?.id }],
      });

      setOpen(false);

      toast.success(t('invoice.financial_adjustment.delete_success'));
    } catch {
      toast.error(t('invoice.financial_adjustment.delete_error'));
    }
  };

  const onDeleteNote = async (noteId: string) => {
    try {
      await mutateDeleteNoteAsync({
        supplierBillingAdjustmentNoteId: noteId,
      });

      await queryClient.invalidateQueries({
        queryKey: [
          'supplierBillingAdjustment',
          { supplierBillingAdjustmentId: adjustmentId },
        ],
      });
    } catch {
      toast.error(t('invoice.financial_adjustment.note.delete_note_error'));
    }
  };

  if (isEditMode) {
    return (
      <FinancialAdjustmentForm adjustmentId={adjustmentId} setOpen={setOpen} />
    );
  }

  const handleDeleteConfirmation = async (confirm: boolean) => {
    setIsOpenDeleteConfirmation(false);

    if (confirm) {
      await onDelete();
    }
  };

  const handleDeleteNoteConfirmation = async (
    params: { confirm: false } | { confirm: true; noteId: string }
  ) => {
    setIsOpenDeleteNoteConfirmation(false);

    if (params.confirm) {
      await onDeleteNote(params.noteId);
    }
  };

  if (!data) {
    return null;
  }

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 pb-0 lg:p-10 lg:pb-0">
          <div className="flex flex-col gap-10 pb-10">
            <SidebarTitle>
              {t('invoice.financial_adjustment_form.add_financial_adjustment')}
            </SidebarTitle>
            <dl className="flex flex-col gap-6">
              <div className="flex flex-row gap-10">
                <div className="flex flex-col gap-1">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('invoice.financial_adjustment.state')}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {data.__typename === 'SupplierBillingAdjustmentDraft'
                      ? t('invoice.financial_adjustment.status.draft')
                      : t('invoice.financial_adjustment.status.validated')}
                  </dd>
                </div>
                <div className="flex flex-col gap-1">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('invoice.financial_adjustment.created_by')}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {data.createdBy.firstname}
                  </dd>
                </div>
                {data.__typename === 'SupplierBillingAdjustmentValidated' ? (
                  <div className="flex flex-col gap-1">
                    <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {t('invoice.financial_adjustment.validated_by')}
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {data.validatedBy.firstname}
                    </dd>
                  </div>
                ) : null}
              </div>
              <div className="flex flex-row gap-10">
                <div className="flex flex-col gap-1">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('invoice.financial_adjustment.charged_to')}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {data.chargedTo === SupplierBillingAdjustmentChargeTo.Choose
                      ? 'Choose'
                      : brand?.name}
                  </dd>
                </div>
              </div>
              <div className="flex flex-row gap-10">
                <div className="flex flex-col gap-1">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('invoice.financial_adjustment.title')}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {data.__typename === 'SupplierBillingAdjustmentDraft'
                      ? data.draftTitle
                      : data.title}
                  </dd>
                </div>
              </div>
              <div className="flex flex-row gap-10">
                <div className="flex flex-col gap-1">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('invoice.financial_adjustment.description')}
                  </dt>
                  <dd className="whitespace-pre-line text-sm text-gray-900">
                    {data.__typename === 'SupplierBillingAdjustmentDraft'
                      ? data.draftDescription
                      : data.description}
                  </dd>
                </div>
              </div>
              <div className="flex flex-row gap-10">
                <div className="flex flex-col gap-1">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('invoice.financial_adjustment.amount_vat_excluded')}
                  </dt>
                  <dd className="whitespace-pre-line text-sm text-gray-900">
                    {data.__typename === 'SupplierBillingAdjustmentDraft'
                      ? data.draftAmountVatExcludedInCents
                        ? formatPrice(
                            data.draftAmountVatExcludedInCents / 100,
                            i18n.language,
                            activeSale?.currency ?? StoreCurrency.Eur
                          )
                        : t('invoice.financial_adjustment.na')
                      : formatPrice(
                          data.amountVatExcludedInCents / 100,
                          i18n.language,
                          activeSale?.currency ?? StoreCurrency.Eur
                        )}
                  </dd>
                </div>
                <div className="flex flex-col gap-1">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('invoice.financial_adjustment.vat_rate')}
                  </dt>
                  <dd className="whitespace-pre-line text-sm text-gray-900">
                    {data.__typename === 'SupplierBillingAdjustmentDraft'
                      ? data.draftVatRate
                        ? formatPercentage(
                            data.draftVatRate / 100,
                            i18n.language
                          )
                        : t('invoice.financial_adjustment.na')
                      : formatPercentage(data.vatRate / 100, i18n.language)}
                  </dd>
                </div>
                <div className="flex flex-col gap-1">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {t('invoice.financial_adjustment.amount_vat_included')}
                  </dt>
                  <dd className="whitespace-pre-line text-sm text-gray-900">
                    {data.__typename === 'SupplierBillingAdjustmentDraft'
                      ? data.draftAmountVatIncludedInCents
                        ? formatPrice(
                            data.draftAmountVatIncludedInCents / 100,
                            i18n.language,
                            activeSale?.currency ?? StoreCurrency.Eur
                          )
                        : t('invoice.financial_adjustment.na')
                      : formatPrice(
                          data.amountVatIncludedInCents / 100,
                          i18n.language,
                          activeSale?.currency ?? StoreCurrency.Eur
                        )}
                  </dd>
                </div>
              </div>
              {data.documents.length > 0 ? (
                <div className="flex flex-row gap-10">
                  <div className="flex flex-col gap-2">
                    <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {t('invoice.financial_adjustment.attachments')}
                    </dt>
                    <dd className="text-sm text-gray-900">
                      <ul className="flex gap-4">
                        {data.documents.map((document) => (
                          <li key={document}>
                            <a
                              href={document}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center rounded border border-gray-100 p-5 text-sm text-gray-700"
                            >
                              <Icon icon="document" className="size-6" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                </div>
              ) : null}
              {isInternalUser ? (
                <div className="flex flex-col gap-6">
                  <div className="text-xl font-bold text-gray-900">
                    {t('invoice.financial_adjustment.notes')}
                  </div>

                  {data.notes && data.notes.length > 0 ? (
                    <ul className="flex flex-col gap-4">
                      {data.notes.map((note) => (
                        <li key={note.id} className="flex flex-row gap-3">
                          <Avatar
                            src={note.createdBy.picture}
                            className="size-8 shrink-0 rounded-full"
                          />
                          <div className="flex flex-col items-start gap-2">
                            <div className="flex flex-col">
                              <div className="flex gap-1 text-sm text-gray-500">
                                <span className="text-gray-900">
                                  {note.createdBy.firstname}
                                </span>
                                {note.createdAt ? (
                                  <>
                                    <span>•</span>
                                    <span>
                                      {formatDate(
                                        new Date(note.createdAt),
                                        'PPpp',
                                        i18n.language
                                      )}
                                    </span>
                                  </>
                                ) : null}
                              </div>
                              <p className="whitespace-pre-line text-sm text-gray-700">
                                {note.content}
                              </p>
                            </div>
                            {note.createdById === user?.sub ? (
                              <Modal
                                open={isOpenDeleteNoteConfirmation}
                                onOpenChange={() =>
                                  handleDeleteNoteConfirmation({
                                    confirm: false,
                                  })
                                }
                              >
                                <button
                                  type="button"
                                  className="text-sm font-semibold text-red-600 focus-visible:ring-2 focus-visible:ring-red-900/30"
                                  onClick={() =>
                                    setIsOpenDeleteNoteConfirmation(true)
                                  }
                                >
                                  {t(
                                    'invoice.financial_adjustment.note.delete'
                                  )}
                                </button>

                                <ModalContent>
                                  <ConfirmDeleteFinancialAdjustmentNoteModal
                                    closeModal={(confirm) => {
                                      if (confirm) {
                                        handleDeleteNoteConfirmation({
                                          confirm: true,
                                          noteId: note.id,
                                        });
                                      } else {
                                        handleDeleteNoteConfirmation({
                                          confirm: false,
                                        });
                                      }
                                    }}
                                  />
                                </ModalContent>
                              </Modal>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <FinancialAdjustmentNoteForm
                    supplierBillingAdjustmentId={
                      data.supplierBillingAdjustmentId
                    }
                  />
                </div>
              ) : null}
            </dl>
          </div>
        </div>
      </div>
      <footer className="flex shrink-0 justify-between p-4 lg:px-10">
        <div className="flex gap-6">
          {data.__typename === 'SupplierBillingAdjustmentDraft' &&
          isInternalUser ? (
            <Button
              type="button"
              onClick={onValidate}
              disabled={
                !isScopeAuthorized(
                  user,
                  'scope.brand.supplier-billing.adjustment.validate'
                )
              }
            >
              {t('invoice.financial_adjustment.validate')}
            </Button>
          ) : null}
          {isInternalUser ? (
            <Button
              type="button"
              appearance="outline"
              onClick={() => setIsEditMode(true)}
              disabled={
                !isScopeAuthorized(
                  user,
                  'scope.brand.supplier-billing.adjustment.update'
                )
              }
            >
              {t('invoice.financial_adjustment.edit')}
            </Button>
          ) : null}
        </div>
        {isInternalUser ? (
          <Modal
            open={isOpenDeleteConfirmation}
            onOpenChange={() => handleDeleteConfirmation(false)}
          >
            <Button
              type="button"
              appearance="danger"
              onClick={() => setIsOpenDeleteConfirmation(true)}
              disabled={
                !isScopeAuthorized(
                  user,
                  'scope.brand.supplier-billing.adjustment.delete'
                )
              }
            >
              {t('invoice.financial_adjustment.delete')}
            </Button>

            <ModalContent>
              <ConfirmDeleteFinancialAdjustmentModal
                closeModal={handleDeleteConfirmation}
              />
            </ModalContent>
          </Modal>
        ) : null}
      </footer>
    </div>
  );
};
